import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Music, Volume2, ArrowRight, Compass, Check, X, PartyPopper } from "lucide-react";
import { childApi } from "../lib/api";
import { playAudioSpec, installAudioUnlock } from "../lib/audio";
import { StaveView } from "../components/Stave";

const KIND_WORDS = { baseline: "Find My Starting Point", progress_check: "Progress Check" };

function DiagQuestion({ q, onAnswer, picked }) {
  const [audioErr, setAudioErr] = useState("");
  const [busy, setBusy] = useState(false);
  const hear = async () => {
    setBusy(true);
    setAudioErr("");
    try {
      await playAudioSpec(q.audio);
    } catch (e) {
      console.error("[ChoirQuest audio] diagnostic playback failed:", e);
      setAudioErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="diagnostic-question">
      {q.audio && (
        <div className="rounded-2xl bg-[#0B192C] p-4 mb-4">
          <button onClick={hear} disabled={busy || picked !== null} data-testid="diagnostic-play-audio"
            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[52px] disabled:opacity-50">
            <Volume2 className="w-5 h-5" aria-hidden /> ▶ Listen
          </button>
          {audioErr && <p role="alert" className="mt-2 text-sm text-[#F2B8C6]" data-testid="diagnostic-audio-error">{audioErr}</p>}
        </div>
      )}
      <p className="font-display text-xl font-bold text-[#0B192C] whitespace-pre-line" data-testid="diagnostic-question-text">{q.text}</p>
      {q.notation && (
        <div className="mt-3">
          <StaveView notation={q.notation} width={Math.min(560, window.innerWidth - 80)} testid="diagnostic-stave" />
        </div>
      )}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {q.options.map((opt, i) => {
          let cls = "border-[#E5DEC9] bg-white text-[#0B192C] hover:border-[#C69B3D]";
          if (picked !== null && i === picked) cls = "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]";
          return (
            <button key={i} onClick={() => onAnswer(i)} disabled={picked !== null}
              data-testid={`diagnostic-option-${i}`}
              className={`text-left px-4 py-3.5 rounded-xl border-2 font-semibold text-sm transition-colors min-h-[52px] ${cls}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro"); // intro | playing | complete
  const [sessionId, setSessionId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [picked, setPicked] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState("");
  const [diagType, setDiagType] = useState("baseline");

  useEffect(() => { installAudioUnlock(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = async () => {
    setError("");
    try {
      const { data } = await childApi.post("/child/diagnostic/start");
      setSessionId(data.session_id);
      setDiagType(data.type);
      if (data.complete) {
        setPhase("complete");
      } else {
        setCurrent(data);
        setPhase("playing");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  const answer = async (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    try {
      const { data } = await childApi.post("/child/diagnostic/answer", {
        session_id: sessionId, question_id: current.question.id, answer: idx,
      });
      setVerdict(data.correct);
      setTimeout(() => {
        setPicked(null);
        setVerdict(null);
        if (data.complete) {
          setPhase("complete");
        } else {
          setCurrent(data);
        }
      }, 900);
    } catch (e) {
      setPicked(null);
      setError(e?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="diagnostic-page">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 py-5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/play/home" className="flex items-center gap-2" data-testid="diagnostic-home-link">
            <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
            <span className="font-display font-bold">ChoirQuest</span>
          </Link>
          {phase === "playing" && current && (
            <span className="text-sm font-semibold text-[#FAF7EE]/80" data-testid="diagnostic-progress">
              Area {current.area_index + 1} of {current.areas_total}
            </span>
          )}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8">
        {phase === "intro" && (
          <div className="text-center space-y-5" data-testid="diagnostic-intro">
            <Compass className="w-14 h-14 text-[#C69B3D] mx-auto" aria-hidden />
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">{KIND_WORDS[diagType] || "Find My Starting Point"}</h1>
            <p className="text-base text-[#566270] max-w-md mx-auto">
              A few short musical puzzles — listening, rhythms and notes — so your choir leader knows
              the best place for you to begin. There are no grades and nothing to pass or fail;
              just answer what you can.
            </p>
            <button onClick={begin} data-testid="diagnostic-start-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[52px]">
              Let's go <ArrowRight className="w-5 h-5" aria-hidden />
            </button>
          </div>
        )}

        {phase === "playing" && current && (
          <div className="space-y-4">
            <div key={current.area}>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C69B3D]" data-testid="diagnostic-area-label">
                {current.area_label}
              </p>
              <div className="mt-3">
                <DiagQuestion q={current.question} onAnswer={answer} picked={picked} />
              </div>
            </div>
            {verdict !== null && (
              <p className={`flex items-center gap-2 text-sm font-bold ${verdict ? "text-[#2D6A4F]" : "text-[#566270]"}`}
                data-testid="diagnostic-verdict">
                {verdict ? <><Check className="w-4 h-4" aria-hidden /> Lovely — next one…</> : <><X className="w-4 h-4" aria-hidden /> No worries — next one…</>}
              </p>
            )}
          </div>
        )}

        {phase === "complete" && (
          <div className="text-center space-y-5" data-testid="diagnostic-complete">
            <PartyPopper className="w-14 h-14 text-[#C69B3D] mx-auto" aria-hidden />
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">All done!</h1>
            <p className="text-base text-[#566270] max-w-md mx-auto">
              Brilliant work. Your choir leader can now see your starting point and will
              unlock the right challenges for you.
            </p>
            <button onClick={() => navigate("/play/home")} data-testid="diagnostic-finish-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px]">
              Back to home
            </button>
          </div>
        )}

        {error && <p role="alert" className="mt-4 text-sm text-[#A62B2B]" data-testid="diagnostic-error">{error}</p>}
      </main>
    </div>
  );
}
